import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {FIELD_TYPE_TO_LEAF_TYPE, FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {StepItemStatus} from 'ui-library/StepNav';

export enum StepKey {
  ANALYSIS_TEMPLATE = 'ANALYSIS_TEMPLATE',
  COLUMNS = 'COLUMNS',
}

export interface DatasetColumnTypeMetadata {
  name?: string;
  columnType?: string;
  isRequired?: boolean;
  templateColumnName?: string;
}

export interface DatasetConfigModalState {
  statusByStep: {[key in StepKey]: StepItemStatus};
  currentStepKey: StepKey;
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>;
  analysisTemplate: string | null;
}

export const INITIAL_STATE: DatasetConfigModalState = {
  statusByStep: {
    [StepKey.COLUMNS]: StepItemStatus.UNVISITED,
    [StepKey.ANALYSIS_TEMPLATE]: StepItemStatus.UNVISITED,
  },
  currentStepKey: StepKey.COLUMNS,
  columnTypeMetadatas: [],
  analysisTemplate: null,
};

export function convertColumnTypeMetadatasToString(
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>
): string {
  return JSON.stringify({datasetColumnTypes: columnTypeMetadatas});
}

export function convertStringToColumnTypeMetadatas(
  columnTypeMetadatasString: string | undefined
): Array<DatasetColumnTypeMetadata> {
  return columnTypeMetadatasString ? JSON.parse(columnTypeMetadatasString).datasetColumnTypes : [];
}

export const PRIMITIVE_COLUMN_TYPES: Array<string> = [
  'boolean',
  'date',
  'datetime',
  'float',
  'integer',
  'json',
  'text',
];

export const PRIMITIVE_COLUMN_TYPE_TO_LEAF_TYPE: {[id: string]: LeafType} = {
  boolean: LeafType.BOOLEAN,
  date: LeafType.DATE,
  datetime: LeafType.DATETIME,
  float: LeafType.DECIMAL,
  integer: LeafType.INTEGER,
  json: LeafType.JSON,
  text: LeafType.TEXT,
};

export const LEAF_TYPE_TO_PRIMITIVE_COLUMN_TYPE: Partial<{[id in LeafType]: string}> = {
  [LeafType.BOOLEAN]: 'boolean',
  [LeafType.DATE]: 'date',
  [LeafType.DATETIME]: 'datetime',
  [LeafType.DECIMAL]: 'float',
  [LeafType.INTEGER]: 'integer',
  [LeafType.JSON]: 'json',
  [LeafType.TEXT]: 'text',
};

export const PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME: {[id: string]: string} = {
  boolean: 'Boolean',
  date: 'Date',
  datetime: 'Datetime',
  float: 'Decimal',
  integer: 'Integer',
  json: 'JSON',
  text: 'Text',
};

function getFieldType(fieldType: FieldType): string {
  const leafType = FIELD_TYPE_TO_LEAF_TYPE[fieldType];
  // only primitives are supported at the moment, default all else to text
  return LEAF_TYPE_TO_PRIMITIVE_COLUMN_TYPE[leafType] ?? 'text';
}

export function convertResultSchemaToDatasetConfigString(resultSchema: ResultSchema): string {
  const resultFields = resultSchema.allFields.filter((field: SchemaField) => !isSchemaFieldArchived(field));
  const columnTypeMetadatas = resultFields.map((field: SchemaField) => ({
    name: field.displayName,
    columnType: getFieldType(field.fieldType),
    isRequired: field.isRequired,
  }));
  return convertColumnTypeMetadatasToString(columnTypeMetadatas);
}
